import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Select,
  DatePicker,
  Typography,
  Modal,
  Input,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useMaterialReturns,
  useAcceptMaterialReturn,
  useRejectMaterialReturn,
} from '../../hooks/useMaterialReturn';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { MaterialReturn } from '../../services/materialReturn.service';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const MaterialReturnsPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { data: departments = [] } = useDepartmentsForSelection();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: returns = [], isLoading } = useMaterialReturns({
    status: statusFilter || undefined,
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
  });

  const acceptMutation = useAcceptMaterialReturn();
  const rejectMutation = useRejectMaterialReturn();

  const columns: ColumnsType<MaterialReturn> = useMemo(
    () => [
      {
        title: 'Return #',
        dataIndex: 'return_number',
        key: 'return_number',
        width: 140,
        render: (text: string) => <Text strong>{text}</Text>,
      },
      {
        title: 'Date',
        dataIndex: 'return_date',
        key: 'return_date',
        width: 120,
        render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'return_status',
        key: 'return_status',
        width: 110,
        render: (s: string) => (
          <Tag color={s === 'Accepted' ? 'green' : s === 'Rejected' ? 'red' : 'orange'}>{s}</Tag>
        ),
      },
      {
        title: 'Work order',
        key: 'work_order',
        width: 140,
        render: (_: unknown, row) =>
          row.work_order_id ? (
            <Link to={`/mfg/work-orders/${row.work_order_id}`}>{row.work_order_id.slice(0, 8)}…</Link>
          ) : (
            '—'
          ),
      },
      {
        title: 'Items',
        key: 'items',
        width: 70,
        render: (_: unknown, row) => row.items?.length ?? 0,
      },
      {
        title: 'Total value',
        dataIndex: 'total_value',
        key: 'total_value',
        width: 110,
        render: (v: number) => formatCurrency(Number(v || 0)),
      },
      {
        title: 'Department',
        dataIndex: 'department_id',
        key: 'department_id',
        width: 160,
        ellipsis: true,
        render: (id: string | null | undefined) => {
          if (!id) return '—';
          const d = departments.find((x) => x.id === id);
          return d ? `${d.department_code} - ${d.department_name}` : id;
        },
      },
      {
        title: 'Reason',
        dataIndex: 'return_reason',
        key: 'return_reason',
        ellipsis: true,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        render: (_: unknown, row) =>
          row.return_status === 'Pending' ? (
            <Space>
              <Button
                type="primary"
                size="small"
                loading={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate(row.id)}
              >
                Accept
              </Button>
              <Button
                size="small"
                danger
                onClick={() => {
                  setRejectId(row.id);
                  setRejectReason('');
                }}
              >
                Reject
              </Button>
            </Space>
          ) : null,
      },
    ],
    [acceptMutation, departments, formatCurrency]
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Material Returns</Title>
      <Text type="secondary">Review and accept returned materials to stock</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            onChange={(v) => setStatusFilter(v || '')}
          >
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Accepted">Accepted</Select.Option>
            <Select.Option value="Rejected">Rejected</Select.Option>
          </Select>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0]?.format('YYYY-MM-DD') || '',
                  dates[1]?.format('YYYY-MM-DD') || '',
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={returns}
          scroll={{ x: 1000 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title="Reject material return"
        open={!!rejectId}
        onCancel={() => setRejectId(null)}
        onOk={() => {
          if (!rejectId) return;
          rejectMutation.mutate({ id: rejectId, reason: rejectReason });
          setRejectId(null);
        }}
        confirmLoading={rejectMutation.isPending}
      >
        <Input.TextArea
          rows={3}
          placeholder="Rejection reason (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default MaterialReturnsPage;
